import { useEffect, useMemo, useState } from 'react'
import Form from 'components/form/Home'
import Button from 'components/home/common/Button'
import { formatShortCurrency } from 'data/helpers/currency'
import { useForm } from 'react-hook-form'
import { formatDefault } from 'data/helpers/dates'
import LogoLink from 'components/logoLink'
import { config } from 'data/config'
import { getDisabledDates } from 'data/helpers/dates'
import { useDispatch, useSelector } from 'react-redux'
import { setAdults, setArrival, setChildren, setDeparture } from 'store/booking-request'
import { asDate } from '@ama-selections/ui/src/helpers/dates'
import { PropertyPageQuery } from 'gql/graphql'
import ControlledDateRangePickerModal, { DateRangeInput } from 'components/dates/ControlledDateRangePickerModal'
import SearchFilter from 'components/searchpage/SearchFilter'
import ControlledNumberInput from 'components/form/Home/ControlledNumberInput'
import Headers from 'components/home/common/typography/Headers'
import Lists from 'components/home/common/typography/Lists'
import { Label } from 'components/home/common/Button'
import { graphql } from 'gql'
import { parseGuests } from 'data/helpers/strings'
import { useQuery } from 'urql'
import { RootState } from 'store'
import { useMutation } from 'react-query'
import ApiClient from 'data/api/api_client'
import { InquiryType } from 'data/types/inquiry'
import useErrorHandler from 'data/hooks/useErrorHandler'
import { triggerInquiryForm } from 'data/helpers/gtm'
import PublicMobileModalFormWithAuth from 'components/PublicMobileModalFormWithAuth'
import { TickIcon } from 'components/icons'
import BookingConditionsTrigger from 'components/modals/triggers/property/BookingConditionsTrigger'
import classNames from 'classnames'
import useAuth from 'data/hooks/User/useAuth'

interface BookingRequestModalProps {
  property: NonNullable<PropertyPageQuery['property']>
  isOpen: boolean
  onClose: () => void
}

type BookingRequestModalFormFields = {
  dates: DateRangeInput | undefined
  adults: number | undefined
  children: number | undefined
  message: string | undefined
}

const BookingRequestModal = ({
  property,
  isOpen,
  onClose,
}: BookingRequestModalProps) => {
  const dispatch = useDispatch()
  const handleError = useErrorHandler()
  const { user } = useAuth()

  const [isChildModalOpen, setIsChildModalOpen] = useState(false)

  const { handleSubmit, control, formState: { errors }, watch, reset, register, setValue, getValues, setError, clearErrors } = useForm<BookingRequestModalFormFields>()

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)

  const [{ data }] = useQuery({
    query: graphql(`
      query Availability ($input: AvailabilityInput!) {
        availability (input: $input) {
          available
          arrival
          departure
          price
          currency
        }
      }
    `),
    variables: {
      input: {
        arrival: formatDefault(watch('dates')?.startDate),
        departure: formatDefault(watch('dates')?.endDate),
        property_id: property?.id,
      },
    },
    pause: !watch('dates')?.startDate || !watch('dates')?.endDate || !property?.id,
  })

  const validForm = useMemo(
    () => data?.availability?.available && watch('dates')?.startDate && watch('dates')?.endDate,
    [data?.availability?.available, watch('dates')?.startDate, watch('dates')?.endDate],
  )

  // Get updates from redux store on open
  const { arrival, departure, adults, children } = useSelector((state: RootState) => ({
    arrival: state.bookingRequest.arrival,
    departure: state.bookingRequest.departure,
    adults: state.bookingRequest.adults,
    children: state.bookingRequest.children,
  }))

  useEffect(() => {
    if (isOpen) {
      reset({
        dates: {
          startDate: asDate(arrival),
          endDate: asDate(departure),
        },
        adults: adults,
        children: children,
      })
    }
  }, [isOpen])

  // Update redux store
  useEffect(() => {
    if (isOpen) {
      dispatch(setArrival(watch('dates')?.startDate))
      dispatch(setDeparture(watch('dates')?.endDate))
      dispatch(setAdults(watch('adults')))
      dispatch(setChildren(watch('children')))
    }
  }, [watch('dates'), watch('adults'), watch('children')])

  const requestMutation = useMutation(
    (details: BookingRequestModalFormFields) => ApiClient.inquiries.createInquiry({
      arrival: formatDefault(details.dates?.startDate),
      departure: formatDefault(details.dates?.endDate),
      adults: details.adults,
      children: details.children ?? 0,
      message: details.message,
      property_id: property.id,
      quoted_price: data?.availability?.price ?? 0,
    }, InquiryType.Specific),
    {
      onSuccess: () => {
        setIsSuccessModalOpen(true)
        triggerInquiryForm({
          email: user!.email,
          countryCode: user!.phone_country_code,
          phone: user!.phone,
        })
        reset({
          dates: {
            startDate: undefined,
            endDate: undefined,
          },
          adults: undefined,
          children: undefined,
          message: undefined,
        })
      },
      onError: (error) => {
        clearErrors()
        handleError(error, setError, {
          'arrival': 'dates',
          'departure': 'dates',
        })
      },
    },
  )

  const onSubmit = (details: BookingRequestModalFormFields) => {
    if (!requestMutation.isLoading && !isSuccessModalOpen) {
      requestMutation.mutate(details)
    }
  }

  return (
    <PublicMobileModalFormWithAuth
      isOpen={isOpen}
      onClose={onClose}
      handleSubmit={handleSubmit}
      isChildModalOpen={isChildModalOpen}
      className={{
        panel: '!max-w-[500px] !pb-0',
      }}
      formProps={{
        onSubmit: (details: BookingRequestModalFormFields) => onSubmit(details),
        className: 'flex flex-col px-24 pt-35 h-full',
        id: 'property-inquiry-form',
      }}
      loginModal={{
        onSubmit: () => onSubmit(getValues()),
      }}
      successModal={{
        button: {
          children: 'Inquiry Received',
          icon: <TickIcon />,
          onClick: () => onClose(),
        },
        text: 'Thank you for contacting us! We will get back to you shortly.',
        isOpen: isSuccessModalOpen,
        onClose: () => setIsSuccessModalOpen(false),
      }}
    >
      <div className="flex justify-center mb-25">
        <LogoLink className="max-w-[110px] w-full" />
      </div>

      {/* Dates */}
      <ControlledDateRangePickerModal
        datePickerProps={{
          control: control,
          name: 'dates',
          disabledDates: getDisabledDates(property?.public_events),
        }}
        selectedDates={watch('dates')}
        onClear={() => setValue('dates', {
          startDate: undefined,
          endDate: undefined,
        })}
        error={errors.dates?.message || data?.availability?.arrival as string}
        onIsOpenChange={(isOpen) => setIsChildModalOpen(isOpen)}
      />

      {/* Guests */}
      <SearchFilter
        open={true}
        placeholder="Who's coming?"
        label="Guests"
        value={isOpen
          ? parseGuests(getValues('adults'), getValues('children'))
          : undefined
        }
        className={{
          container: 'mt-10',
        }}
      >
        <ControlledNumberInput
          placeholder="0"
          label="Adults"
          sublabel="18 or older"
          control={control}
          name="adults"
          error={errors.adults}
          className={{ container: 'px-8 pt-8 pb-12' }}
        />

        <ControlledNumberInput
          name="children"
          control={control}
          placeholder="0"
          label="Children"
          sublabel="Under 18"
          error={errors.children}
          className={{ container: 'px-8 pt-8 pb-12' }}
        />
      </SearchFilter>

      <div
        className={classNames(
          'flex flex-col items-center text-center mt-25 gap-15',
          validForm ? 'block' : 'hidden',
        )}
        aria-hidden={!validForm}
      >
        <Headers.LG>
          {
            data?.availability?.price
              ? `Total Rent: ${formatShortCurrency(data?.availability?.price, data?.availability?.currency)}`
              : 'Price upon request'
          }
        </Headers.LG>

        <BookingConditionsTrigger
          property={property}
          availability={data?.availability}
        />
      </div>

      <hr className="h-2 border-none my-25 bg-grey-150" />

      <Headers.BaseTitle className="mb-15 text-18 lg:text-24">
        What&rsquo;s next?
      </Headers.BaseTitle>

      <ol className="flex flex-col gap-8 list-decimal list-inside text-grey-750 mb-25">
        <Lists.MD>We confirm your booking within 24 hours</Lists.MD>
        <Lists.MD>Sign booking form &amp; process payment</Lists.MD>
        <Lists.MD>Plan your trip with your dedicated concierge</Lists.MD>
      </ol>

      <Form.Textarea
        {...register('message')}
        label="Message"
        className={{
          container: 'mb-25',
        }}
        placeholder="We&rsquo;d love to know more about your trip. Leave us a message here."
        error={errors.message}
      />

      <div className="sticky bottom-0 z-10 flex flex-col items-center px-24 pt-10 -mx-24 bg-white pb-safe-or-10">
        <Button
          style="modal"
          block
          type="submit"
          isLoading={requestMutation.isLoading}
          className={{
            button: 'max-w-xs',
          }}
          disabled={!data?.availability?.available}
        >
          {validForm
            ? 'inquire now'
            : 'add dates for price'
          }
        </Button>


        <Label variant="dark-grey" className={{ label: 'mb-15' }}>
          Or call us at <a className="underline hover:text-grey-900" href={`tel:${config.amaSelectionsPhoneNumber}`}>{config.amaSelectionsDisplayPhoneNumber}</a>
        </Label>
      </div>
    </PublicMobileModalFormWithAuth>
  )
}

export default BookingRequestModal
